/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ASİSTAN TURUNUN SIRALI BLOK MODELİ (1 Eki 2026) — SAF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün sahibi: AI önce bir araç çağırıp kart üretiyor, sonra düşünmeye devam
 * edip metin yazıyordu; ekranda kart önce görünüyor, metin akarken kartın
 * ÜSTÜNE yazılıyor ve kart sürekli aşağı itiliyordu — "inanılmaz saçma bir
 * görüntü". Sebep: tur TEK bir metin + AYRI bir kart listesi olarak
 * çiziliyordu (metin hep kartların üstünde).
 *
 * Şimdi tur, OLUŞTUĞU SIRAYLA bir blok listesi:
 *
 *     [etkinlik paneli] → metin → kart → metin → onay kartı → …  → [kaynaklar]
 *
 *   · Yeni metin her zaman EN ALTTAKİ metin bloğuna ya da yeni bir metin
 *     bloğuna akar — daha önce çizilmiş bir kartın üstüne asla girmez.
 *   · Kart oluştuğu noktada sabit kalır (yalnız SONA ekleme; kayma yok).
 *   · Daktilo blokları SIRAYLA açar: bir kart, kendinden önceki metin yazılıp
 *     bitmeden görünmez (yoksa yazılmakta olan metin kartı yine aşağı iterdi).
 *
 * Aynı model canlı akışta (`agent-console.tsx`, kareler geldikçe) ve kalıcı
 * kayıttan yeniden çizimde (`agent-turns.ts` → `replyBlocksFromRows`) — iki yol
 * aynı sırayı üretir. Testi `apps/api/src/agent/reply-blocks.spec.ts`.
 */

export type AgentReplyBlock =
  | { kind: 'text'; key: string; text: string }
  /** Araç sonucunun kartı — veri turun adımlarında (`toolCallId` ile). */
  | { kind: 'card'; key: string; toolCallId: string }
  /** Onay kartı — veri turun önerilerinde (`proposalId` ile). */
  | { kind: 'proposal'; key: string; proposalId: string };

const PARAGRAPH = '\n\n';

/**
 * Metni SONA ekler: son blok metinse ona, değilse yeni bir metin bloğuna.
 *
 * `paragraph` — arada başka bir şey oldu (araç, düşünme, yeni model isteği):
 * metin son metin bloğuna yeni paragraf olarak eklenir. Aynı yanıtın parçaları
 * (alıntılarla bölünmüş metin blokları, akış parçaları) arasına ayraç girmez.
 */
export function appendReplyText(
  blocks: readonly AgentReplyBlock[],
  text: string,
  options: { paragraph?: boolean } = {},
): AgentReplyBlock[] {
  if (text.length === 0) return [...blocks];
  const last = blocks.at(-1);
  if (last?.kind === 'text') {
    const joined = options.paragraph && last.text.length > 0 ? `${last.text}${separator(last.text)}${text}` : `${last.text}${text}`;
    return [...blocks.slice(0, -1), { ...last, text: joined }];
  }
  const index = blocks.filter((block) => block.kind === 'text').length;
  return [...blocks, { kind: 'text', key: `t${index}`, text }];
}

function separator(previous: string): string {
  if (previous.endsWith(PARAGRAPH)) return '';
  return previous.endsWith('\n') ? '\n' : PARAGRAPH;
}

/** Kartı SONA ekler — aynı çağrının kartı zaten yerindeyse değişmez. */
export function appendReplyCard(blocks: readonly AgentReplyBlock[], toolCallId: string): AgentReplyBlock[] {
  if (blocks.some((block) => block.kind === 'card' && block.toolCallId === toolCallId)) return [...blocks];
  return [...blocks, { kind: 'card', key: `c:${toolCallId}`, toolCallId }];
}

/** Onay kartını SONA ekler — zaten yerindeyse değişmez. */
export function appendReplyProposal(blocks: readonly AgentReplyBlock[], proposalId: string): AgentReplyBlock[] {
  if (blocks.some((block) => block.kind === 'proposal' && block.proposalId === proposalId)) return [...blocks];
  return [...blocks, { kind: 'proposal', key: `p:${proposalId}`, proposalId }];
}

/** Turun metni (kopyalama, sayı denetimi) — metin blokları paragrafla. */
export function replyTextOf(blocks: readonly AgentReplyBlock[]): string {
  return blocks
    .filter((block): block is Extract<AgentReplyBlock, { kind: 'text' }> => block.kind === 'text')
    .map((block) => block.text)
    .join(PARAGRAPH);
}

/** Daktilonun saydığı toplam harf (metin bloklarının uzunlukları, ayraçsız). */
export function replyTextLength(blocks: readonly AgentReplyBlock[]): number {
  let total = 0;
  for (const block of blocks) if (block.kind === 'text') total += block.text.length;
  return total;
}

/**
 * Daktilonun YAZABİLECEĞİ son harf. Son blok metinse ve akış sürüyorsa yalnız
 * tamamlanmış birime kadar (`settledEnd`); önceki metin blokları — arkalarında
 * kart var — zaten bitmiş, tamamı yazılabilir.
 */
export function typingTarget(blocks: readonly AgentReplyBlock[], finished: boolean): number {
  let total = 0;
  blocks.forEach((block, index) => {
    if (block.kind !== 'text') return;
    total += !finished && index === blocks.length - 1 ? settledEnd(block.text) : block.text.length;
  });
  return total;
}

/**
 * Ekrana giden bloklar: metin `typed` harfe kadar; YAZILMAMIŞ metnin ardındaki
 * hiçbir blok görünmez (kart kendinden önceki metin bitmeden belirmez).
 */
export function projectReplyBlocks(blocks: readonly AgentReplyBlock[], typed: number): AgentReplyBlock[] {
  const out: AgentReplyBlock[] = [];
  let remaining = Math.max(0, typed);
  for (const block of blocks) {
    if (block.kind !== 'text') {
      out.push(block);
      continue;
    }
    if (remaining >= block.text.length) {
      out.push(block);
      remaining -= block.text.length;
      continue;
    }
    if (remaining > 0) out.push({ ...block, text: block.text.slice(0, remaining) });
    break;
  }
  return out;
}

/**
 * Ham metnin YAZILABİLİR sonu: son tamamlanmış birimin bittiği yer — satır sonu
 * ya da cümle sonu (noktadan sonra BOŞLUK: `3.5`, `v1.2` cümle sonu değil).
 * Yarım satır / yarım markdown ekrana düşmesin diye.
 */
export function settledEnd(text: string): number {
  for (let i = text.length - 1; i >= 0; i -= 1) {
    const ch = text[i];
    if (ch === '\n') return i + 1;
    if ((ch === '.' || ch === '!' || ch === '?' || ch === ':') && /\s/.test(text[i + 1] ?? '')) {
      return i + 1;
    }
  }
  return 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// Kalıcı kayıttan yeniden kurma
// ═══════════════════════════════════════════════════════════════════════════

/** Turun bir asistan satırı — `replyBlocksFromRows` girdisi. */
export interface ReplyRowInput {
  /** Satırın ARAYÜZ blokları (metin, araç çağrısı, imzasız düşünme notu…). */
  content: readonly unknown[];
  /** Satırın kart kayıtlarındaki DOSYA kartları (`file_output`) — `tool_use` bloğu yok. */
  fileCalls: readonly string[];
  /** Bu satırda doğan onay kartları (doğuş sırasıyla). */
  proposalIds: readonly string[];
}

/**
 * Bir turun satırları → sıralı bloklar. Canlı akışla AYNI kural:
 *
 *   · metin → son metin bloğuna (satır başında ya da arada araç/düşünme
 *     olduysa yeni paragraf); aynı satırın alıntıyla bölünmüş metin blokları
 *     ayraçsız birleşir
 *   · `tool_use` → kartı varsa (`cardCalls`) TAM o noktada kart bloğu
 *   · satırın sonunda: o satırda üretilen dosya kartları, sonra onay kartları
 */
export function replyBlocksFromRows(rows: readonly ReplyRowInput[], cardCalls: ReadonlySet<string>): AgentReplyBlock[] {
  let blocks: AgentReplyBlock[] = [];
  for (const row of rows) {
    let paragraph = true;
    for (const raw of row.content) {
      if (!raw || typeof raw !== 'object') continue;
      const block = raw as { type?: unknown; text?: unknown; id?: unknown };
      if (block.type === 'text' && typeof block.text === 'string') {
        blocks = appendReplyText(blocks, block.text, { paragraph });
        paragraph = false;
        continue;
      }
      if (block.type === 'tool_use') {
        paragraph = true;
        if (typeof block.id === 'string' && cardCalls.has(block.id)) blocks = appendReplyCard(blocks, block.id);
        continue;
      }
      /* Düşünme notu ve sunucu adımı (`mentorivo_step`) — araya bir şey girdi. */
      if (block.type === 'thinking' || block.type === 'mentorivo_step') paragraph = true;
    }
    for (const id of row.fileCalls) if (cardCalls.has(id)) blocks = appendReplyCard(blocks, id);
    for (const id of row.proposalIds) blocks = appendReplyProposal(blocks, id);
  }
  return blocks;
}
